import React, { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { TransientState } from '../../../storage';
import { Box, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import {
    AdminMlAwsLaunchTemplates as AdminMlAwsLaunchTemplatesApi,
    AdminMlRegions as AdminMlRegionsApi,
} from '../../../api/admin-ml';
import { AdminMenuBar } from '../..';

const DEFAULT_REGION = 'us-east-1';

const HEAD_CELL = { fontWeight: 600, whiteSpace: 'nowrap', py: 0.5, px: 1 };
const DATA_CELL = { py: 0.5, px: 1 };

function AwsTemplate() {
    const transient = useRecoilValue(TransientState);
    const { regions, awsLaunchTemplates } = transient.infrastructure;

    useEffect(() => {
        AdminMlRegionsApi();
        AdminMlAwsLaunchTemplatesApi(undefined, true);
    }, []);

    const refresh = () => {
        AdminMlRegionsApi();
        AdminMlAwsLaunchTemplatesApi(undefined, true);
    };

    const templatesByRegion = {};
    for (const tpl of awsLaunchTemplates) {
        const r = tpl.region ?? DEFAULT_REGION;
        (templatesByRegion[r] ??= []).push(tpl);
    }

    const allRegions = [...new Set([...regions, ...Object.keys(templatesByRegion)])].sort();

    return (
        <Stack direction='column' sx={{ width: '100%', height: '100%', minHeight: 0 }}>
            <AdminMenuBar apiItems={[{ label: 'Refresh', onClick: refresh }]} />
            <Box sx={{ overflow: 'auto', flex: 1 }}>
                {allRegions.length === 0 ? (
                    Array.from({ length: 5 }, (_, i) => (
                        <Skeleton key={i} variant='text' sx={{ width: 200, height: 38 }} />
                    ))
                ) : (
                    <Table
                        size='small'
                        sx={{ '& .MuiTableCell-root': { border: '1px solid', borderColor: 'divider' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={HEAD_CELL}>Region</TableCell>
                                <TableCell sx={HEAD_CELL}>Name</TableCell>
                                <TableCell sx={HEAD_CELL}>ID</TableCell>
                                <TableCell sx={HEAD_CELL}>Default Ver</TableCell>
                                <TableCell sx={HEAD_CELL}>Latest Ver</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {allRegions.map((region) => {
                                const templates = templatesByRegion[region] ?? [];
                                return (
                                    <React.Fragment key={region}>
                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                            <TableCell colSpan={5} sx={{ py: 0.5, px: 1 }}>
                                                <Stack direction='row' alignItems='center' spacing={1}>
                                                    <Typography variant='body2' fontWeight={400}>
                                                        {region}
                                                    </Typography>
                                                    {templates.length > 0 && (
                                                        <Typography variant='caption' color='text.secondary'>
                                                            {templates.length} template
                                                            {templates.length > 1 ? 's' : ''}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                        {templates.map((tpl) => (
                                            <TableRow key={tpl.id} hover>
                                                <TableCell sx={DATA_CELL} />
                                                <TableCell sx={DATA_CELL}>{tpl.name}</TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...DATA_CELL,
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.75rem',
                                                    }}>
                                                    {tpl.id}
                                                </TableCell>
                                                <TableCell sx={DATA_CELL}>v{tpl.version ?? '—'}</TableCell>
                                                <TableCell sx={DATA_CELL}>v{tpl.latestVersion ?? '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Box>
        </Stack>
    );
}

export { AwsTemplate };
